/*
 * Колонки таблиц кластеров и моделей — одни и те же на главной странице (core Table)
 * и на страницах каталога (CatalogTable): обе работают со строками вида { entity }.
 * Ячейки со статусами передаются снаружи: на главной они читают уже загруженные
 * данные строки, в каталоге грузят их сами.
 */
import { ReactNode } from 'react';
import {
  Entity,
  RELATION_OWNED_BY,
  parseEntityRef,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import { Link, TableColumn } from '@backstage/core-components';
import {
  EntityRefLink,
  EntityRefLinks,
  getEntityRelations,
} from '@backstage/plugin-catalog-react';
import { Typography } from '@material-ui/core';
import {
  AIRFLOW_DAGS_REPO_ANNOTATION,
  AIRFLOW_URL_ANNOTATION,
  CLUSTER_DOMAIN_ANNOTATION,
  CLUSTER_ENV_ANNOTATION,
  CLUSTER_TARGET_ANNOTATION,
  CLUSTER_TARGET_LABELS,
  DATASTORE_CLUSTER_ANNOTATION,
  DATASTORE_ENDPOINT_ANNOTATIONS,
  DEFECTDOJO_URL_ANNOTATION,
  RUSTFS_POOL_ANNOTATION,
  RUSTFS_REPLICAS_ANNOTATION,
  RUSTFS_URL_ANNOTATION,
  SERVICE_LANGUAGE_ANNOTATION,
  annotation,
  envRank,
} from '../../lib/viva';
import { ServiceLinks } from './cells';

type Row = { entity: Entity };
type Cell<T> = (row: T) => ReactNode;

const text = (value?: string) =>
  value ? (
    <Typography variant="body2" noWrap>
      {value}
    </Typography>
  ) : (
    <Typography variant="body2" color="textSecondary">
      —
    </Typography>
  );

/** spec.owner / spec.system могут быть без kind («viva»): дописываем kind и namespace. */
const fullRef = (value: unknown, defaultKind: string): string =>
  stringifyEntityRef(
    parseEntityRef(String(value), { defaultKind, defaultNamespace: 'default' }),
  );

const byString =
  <T extends Row>(pick: (entity: Entity) => string | undefined) =>
  (a: T, b: T) =>
    (pick(a.entity) ?? '').localeCompare(pick(b.entity) ?? '', 'ru');

export function nameColumn<T extends Row>(title: string): TableColumn<T> {
  return {
    title,
    highlight: true,
    cellStyle: { whiteSpace: 'nowrap' },
    customSort: byString(e => e.metadata.title ?? e.metadata.name),
    render: ({ entity }) => <EntityRefLink entityRef={entity} />,
  };
}

export function ownerColumn<T extends Row>(): TableColumn<T> {
  return {
    title: 'Владелец',
    customSort: byString(e => String(e.spec?.owner ?? '')),
    render: ({ entity }) => {
      const owners = getEntityRelations(entity, RELATION_OWNED_BY);
      if (owners.length > 0) {
        return <EntityRefLinks entityRefs={owners} defaultKind="group" />;
      }
      const owner = entity.spec?.owner;
      return owner ? (
        <EntityRefLink entityRef={fullRef(owner, 'group')} />
      ) : (
        text()
      );
    },
  };
}

export function clusterColumns<T extends Row>(
  argoCell: Cell<T>,
): TableColumn<T>[] {
  return [
    nameColumn('Кластер'),
    {
      title: 'Окружение',
      customSort: (a, b) =>
        envRank(annotation(a.entity, CLUSTER_ENV_ANNOTATION)) -
        envRank(annotation(b.entity, CLUSTER_ENV_ANNOTATION)),
      render: ({ entity }) => {
        const target = annotation(entity, CLUSTER_TARGET_ANNOTATION);
        return (
          <>
            {text(annotation(entity, CLUSTER_ENV_ANNOTATION))}
            {target && (
              <Typography variant="caption" color="textSecondary" noWrap>
                {CLUSTER_TARGET_LABELS[target] ?? target}
              </Typography>
            )}
          </>
        );
      },
    },
    {
      title: 'Домен',
      customSort: byString(e => annotation(e, CLUSTER_DOMAIN_ANNOTATION)),
      render: ({ entity }) => {
        const domain = annotation(entity, CLUSTER_DOMAIN_ANNOTATION);
        return domain ? (
          <Link to={`https://${domain}`} style={{ overflowWrap: 'anywhere' }}>
            {domain}
          </Link>
        ) : (
          text()
        );
      },
    },
    { title: 'ArgoCD', sorting: false, render: argoCell },
    {
      title: 'Сервисы',
      sorting: false,
      render: ({ entity }) => <ServiceLinks entity={entity} />,
    },
    ownerColumn(),
  ];
}

/** Колонки баз данных и брокеров: страницы однотипные, поэтому колонки «Тип» нет. */
export function datastoreColumns<T extends Row>(
  nameTitle: string,
  argoCell: Cell<T>,
  /** заголовок последней колонки состояния: ArgoCD у хранилищ на операторах, сервис — у managed */
  statusTitle = 'ArgoCD',
): TableColumn<T>[] {
  return [
    nameColumn(nameTitle),
    {
      title: 'Кластер',
      customSort: byString(e => annotation(e, DATASTORE_CLUSTER_ANNOTATION)),
      render: ({ entity }) => {
        const cluster = annotation(entity, DATASTORE_CLUSTER_ANNOTATION);
        return cluster ? (
          <EntityRefLink entityRef={`resource:default/${cluster}`} />
        ) : (
          text()
        );
      },
    },
    {
      title: 'Адрес в кластере',
      sorting: false,
      render: ({ entity }) => {
        const endpoint = DATASTORE_ENDPOINT_ANNOTATIONS.map(a =>
          annotation(entity, a),
        ).find(Boolean);
        return endpoint ? (
          <Typography
            variant="body2"
            style={{ fontFamily: 'monospace', overflowWrap: 'anywhere' }}
          >
            {endpoint}
          </Typography>
        ) : (
          text()
        );
      },
    },
    { title: statusTitle, sorting: false, render: argoCell },
    ownerColumn(),
  ];
}

export function modelColumns<T extends Row>(cells: {
  champion: Cell<T>;
  serving: Cell<T>;
}): TableColumn<T>[] {
  return [
    nameColumn('Модель'),
    { title: 'Сервинг', sorting: false, render: cells.serving },
    { title: 'Чемпион', sorting: false, render: cells.champion },
    {
      title: 'Стадия',
      customSort: byString(e => String(e.spec?.lifecycle ?? '')),
      render: ({ entity }) =>
        text(
          entity.spec?.lifecycle ? String(entity.spec.lifecycle) : undefined,
        ),
    },
    ownerColumn(),
    {
      title: 'Система',
      customSort: byString(e => String(e.spec?.system ?? '')),
      render: ({ entity }) =>
        entity.spec?.system ? (
          <EntityRefLink entityRef={fullRef(entity.spec.system, 'system')} />
        ) : (
          text()
        ),
    },
  ];
}

/** Сервисы классической разработки: язык, кластер, окружения (Application ArgoCD), владелец. */
/** Раздел «JupyterHub»: хаб, кластер, приложение ArgoCD, серверы пользователей, владелец. */
export function jupyterhubColumns<T extends Row>(
  argoCell: Cell<T>,
  serversCell: Cell<T>,
): TableColumn<T>[] {
  return [
    nameColumn('JupyterHub'),
    {
      title: 'Кластер',
      customSort: byString(e => annotation(e, DATASTORE_CLUSTER_ANNOTATION)),
      render: ({ entity }) => {
        const cluster = annotation(entity, DATASTORE_CLUSTER_ANNOTATION);
        return cluster ? (
          <EntityRefLink
            entityRef={`resource:default/${cluster}`}
            title={cluster}
          />
        ) : (
          text()
        );
      },
    },
    {
      title: 'Адрес',
      sorting: false,
      render: ({ entity }) => {
        const url = entity.metadata.links?.find(
          l => l.title === 'JupyterHub',
        )?.url;
        return url ? (
          <Link to={url}>{url.replace(/^https?:\/\//, '')}</Link>
        ) : (
          text()
        );
      },
    },
    { title: 'ArgoCD', sorting: false, render: argoCell },
    { title: 'Серверы', sorting: false, render: serversCell },
    ownerColumn(),
  ];
}

/** Раздел «Airflow»: инстансы (type airflow) — адрес, репозиторий DAG'ов, ArgoCD. */
export function airflowColumns<T extends Row>(
  argoCell: Cell<T>,
): TableColumn<T>[] {
  return [
    nameColumn('Airflow'),
    {
      title: 'Кластер',
      customSort: byString(e => annotation(e, DATASTORE_CLUSTER_ANNOTATION)),
      render: ({ entity }) => {
        const cluster = annotation(entity, DATASTORE_CLUSTER_ANNOTATION);
        return cluster ? (
          <EntityRefLink
            entityRef={`resource:default/${cluster}`}
            title={cluster}
          />
        ) : (
          text()
        );
      },
    },
    {
      title: 'Адрес',
      sorting: false,
      render: ({ entity }) => {
        const url = annotation(entity, AIRFLOW_URL_ANNOTATION);
        return url ? (
          <Link to={url}>{url.replace(/^https?:\/\//, '')}</Link>
        ) : (
          text()
        );
      },
    },
    {
      title: "DAG'и",
      customSort: byString(e => annotation(e, AIRFLOW_DAGS_REPO_ANNOTATION)),
      render: ({ entity }) => {
        const slug = annotation(entity, AIRFLOW_DAGS_REPO_ANNOTATION);
        const url = entity.metadata.links?.find(
          l => l.title === "Репозиторий DAG'ов",
        )?.url;
        if (!slug) return text();
        return url ? <Link to={url}>{slug}</Link> : text(slug);
      },
    },
    { title: 'ArgoCD', sorting: false, render: argoCell },
    ownerColumn(),
  ];
}

/** Раздел «Feast»: Feast кластеров (type feast) и репозитории признаков (type feature-store). */
/** DefectDojo кластеров: кластер, адрес, чьи пайплайны шлют сюда отчёты, ArgoCD. */
export function defectdojoColumns<T extends Row>(
  argoCell: Cell<T>,
  sendersCell: Cell<T>,
): TableColumn<T>[] {
  return [
    nameColumn('DefectDojo'),
    {
      title: 'Кластер',
      customSort: byString(e => annotation(e, DATASTORE_CLUSTER_ANNOTATION)),
      render: ({ entity }) => {
        const cluster = annotation(entity, DATASTORE_CLUSTER_ANNOTATION);
        return cluster ? (
          <EntityRefLink
            entityRef={`resource:default/${cluster}`}
            title={cluster}
          />
        ) : (
          text()
        );
      },
    },
    {
      title: 'Адрес',
      sorting: false,
      render: ({ entity }) => {
        const url = annotation(entity, DEFECTDOJO_URL_ANNOTATION);
        return url ? (
          <Link to={url}>
            {url.replace(/^https?:\/\//, '').replace(/\/$/, '')}
          </Link>
        ) : (
          text()
        );
      },
    },
    { title: 'Отчёты кластеров', sorting: false, render: sendersCell },
    { title: 'ArgoCD', sorting: false, render: argoCell },
    ownerColumn(),
  ];
}

export function feastColumns<T extends Row>(
  argoCell: Cell<T>,
  stateCell: Cell<T>,
): TableColumn<T>[] {
  const isFeast = (e: Entity) => e.spec?.type === 'feast';
  return [
    nameColumn('Feast / репозиторий'),
    {
      title: 'Что это',
      customSort: byString(e => String(e.spec?.type ?? '')),
      render: ({ entity }) =>
        text(isFeast(entity) ? 'Feast кластера' : 'репозиторий признаков'),
    },
    {
      title: 'Кластер',
      customSort: byString(e => annotation(e, DATASTORE_CLUSTER_ANNOTATION)),
      render: ({ entity }) => {
        const cluster = annotation(entity, DATASTORE_CLUSTER_ANNOTATION);
        return cluster ? (
          <EntityRefLink
            entityRef={`resource:default/${cluster}`}
            title={cluster}
          />
        ) : (
          text()
        );
      },
    },
    {
      title: 'Адрес',
      sorting: false,
      render: ({ entity }) => {
        const url = entity.metadata.links?.find(
          l => l.title === 'Feast UI',
        )?.url;
        return url ? (
          <Link to={url}>{url.replace(/^https?:\/\//, '')}</Link>
        ) : (
          text()
        );
      },
    },
    { title: 'ArgoCD', sorting: false, render: argoCell },
    {
      title: 'Материализация',
      sorting: false,
      render: row => (isFeast(row.entity) ? stateCell(row) : text()),
    },
    ownerColumn(),
  ];
}

export function serviceColumns<T extends Row>(
  argoCell: Cell<T>,
  actionsCell?: Cell<T>,
): TableColumn<T>[] {
  return [
    nameColumn('Сервис'),
    {
      title: 'Язык',
      customSort: byString(e => annotation(e, SERVICE_LANGUAGE_ANNOTATION)),
      render: ({ entity }) =>
        text(annotation(entity, SERVICE_LANGUAGE_ANNOTATION)),
    },
    {
      title: 'Кластер',
      customSort: byString(e => annotation(e, DATASTORE_CLUSTER_ANNOTATION)),
      render: ({ entity }) => {
        const cluster = annotation(entity, DATASTORE_CLUSTER_ANNOTATION);
        return cluster ? (
          <EntityRefLink
            entityRef={`resource:default/${cluster}`}
            title={cluster}
          />
        ) : (
          text()
        );
      },
    },
    { title: 'Окружения', sorting: false, render: argoCell },
    {
      title: 'Стадия',
      customSort: byString(e => String(e.spec?.lifecycle ?? '')),
      render: ({ entity }) =>
        text(
          entity.spec?.lifecycle ? String(entity.spec.lifecycle) : undefined,
        ),
    },
    ownerColumn(),
    ...(actionsCell
      ? [
          {
            title: '',
            sorting: false,
            width: '48px',
            render: actionsCell,
          } as TableColumn<T>,
        ]
      : []),
  ];
}

/** Режим RustFS по числу подов: 1 — standalone, больше — распределённый (erasure coding). */
export const rustfsMode = (replicas: string | undefined): string => {
  const n = Number(replicas || 1);
  return n > 1 ? `распределённый, ${n} подов` : 'standalone, 1 под';
};

/** RustFS кластеров: кластер, режим, пул узлов, консоль, ArgoCD. */
export function rustfsColumns<T extends Row>(
  argoCell: Cell<T>,
): TableColumn<T>[] {
  return [
    nameColumn('RustFS'),
    {
      title: 'Кластер',
      customSort: byString(e => annotation(e, DATASTORE_CLUSTER_ANNOTATION)),
      render: ({ entity }) => {
        const cluster = annotation(entity, DATASTORE_CLUSTER_ANNOTATION);
        return cluster ? (
          <EntityRefLink
            entityRef={`resource:default/${cluster}`}
            title={cluster}
          />
        ) : (
          text()
        );
      },
    },
    {
      title: 'Режим',
      customSort: byString(e => annotation(e, RUSTFS_REPLICAS_ANNOTATION)),
      render: ({ entity }) =>
        text(rustfsMode(annotation(entity, RUSTFS_REPLICAS_ANNOTATION))),
    },
    {
      title: 'Пул узлов',
      customSort: byString(e => annotation(e, RUSTFS_POOL_ANNOTATION)),
      render: ({ entity }) =>
        text(annotation(entity, RUSTFS_POOL_ANNOTATION) || 'общие узлы'),
    },
    {
      title: 'Консоль',
      sorting: false,
      render: ({ entity }) => {
        const url = annotation(entity, RUSTFS_URL_ANNOTATION);
        return url ? (
          <Link to={url}>
            {url.replace(/^https?:\/\//, '').replace(/\/$/, '')}
          </Link>
        ) : (
          text()
        );
      },
    },
    { title: 'ArgoCD', sorting: false, render: argoCell },
    ownerColumn(),
  ];
}
